// Gate Manager - Controls state transitions for debugging
// ✅ NO MAGIC STRINGS: Use typed enums and constants

export interface GateConfig {
  enabled: boolean;
  closed: boolean;
  description: string;
}

export interface GatesConfig {
  [gateName: string]: GateConfig;
}

export class GateManager {
  private gates: Map<string, GateConfig> = new Map();
  private eventHandlers: Map<
    string,
    Set<(gateName: string, closed: boolean) => void>
  > = new Map();

  /**
   * Initialize gates from configuration
   */
  initialize(gatesConfig: GatesConfig): void {
    this.gates.clear();
    for (const [name, config] of Object.entries(gatesConfig)) {
      this.gates.set(name, { ...config });
    }
  }

  /**
   * Check if a gate is closed (blocks transition)
   */
  isGateClosed(gateName: string): boolean {
    const gate = this.gates.get(gateName);
    if (!gate || !gate.enabled) {
      return false; // Gate doesn't exist or is disabled, allow transition
    }
    return gate.closed;
  }

  /**
   * Close a gate (block transitions)
   */
  closeGate(gateName: string): void {
    const gate = this.gates.get(gateName);
    if (gate && gate.enabled) {
      gate.closed = true;
      this.emitGateEvent(gateName, true);
    }
  }

  /**
   * Open a gate (allow transitions)
   */
  openGate(gateName: string): void {
    const gate = this.gates.get(gateName);
    if (gate && gate.enabled) {
      gate.closed = false;
      this.emitGateEvent(gateName, false);
    }
  }

  /**
   * Get gate status
   */
  getGateStatus(gateName: string): GateConfig | undefined {
    return this.gates.get(gateName);
  }

  /**
   * Get all gate statuses
   */
  getGateStatuses(): Record<string, GateConfig> {
    const result: Record<string, GateConfig> = {};
    for (const [name, config] of this.gates.entries()) {
      result[name] = { ...config };
    }
    return result;
  }

  /**
   * Subscribe to gate events
   */
  onGateChange(
    gateName: string,
    handler: (gateName: string, closed: boolean) => void,
  ): () => void {
    if (!this.eventHandlers.has(gateName)) {
      this.eventHandlers.set(gateName, new Set());
    }
    this.eventHandlers.get(gateName)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.eventHandlers.get(gateName)?.delete(handler);
    };
  }

  /**
   * Emit gate event
   */
  private emitGateEvent(gateName: string, closed: boolean): void {
    const handlers = this.eventHandlers.get(gateName);
    if (handlers) {
      handlers.forEach((handler) => handler(gateName, closed));
    }
  }
}
